/* OneAmerica AssetCare California Insurance Application */
import { sharedOneAmericaSections } from './oneAmericaAppSharedSections';
import { sharedOneAmericaQuestions } from './oneAmericaAppSharedQuestions';
import { oneAmericaSharedDefs } from './oneAmericaSharedDefs';
import { healthDataSections } from './one-america-ca-health-data/healthDataSections';
import { questionsCategoryA } from './one-america-ca-health-data/questionsCategoryA';
import { questionsCategoryB } from './one-america-ca-health-data/questionsCategoryB';
import { questionsCategoryC } from './one-america-ca-health-data/questionsCategoryC';
import { questionsCategoryF } from './one-america-ca-health-data/questionsCategoryF';
import { questionsCategoryK } from './one-america-ca-health-data/questionsCategoryK';
import { oneAmericaPreApplicationFormDefinition } from '@shared/policy-application/oneAmericaPreApplicationFormDef';
import { FundingPolicyType } from '@shared/appModel';

const {
  firstProposedInsuredUsCitizen,
  secondProposedInsuredUsCitizen,
  ownerUsCitizen,
  coverageHasOtherLtc,
  coverageHadLtcLastYear,
  coverageLtcCompanyName,
  coverageLtcLapseDate,
  coverageHasExistingInsurance,
  medicalHeartConditionFirstInsured,
  medicalHeartConditionSecondInsured,
  medicalCancerFirstInsured,
  medicalCancerSecondInsured,
  medicalDiabetesFirstInsured,
  medicalDiabetesSecondInsured,
  medicalLungDisorderFirstInsured,
  medicalLungDisorderSecondInsured,
  medicalIntestinalFirstInsured,
  medicalIntestinalSecondInsured,
  medicalBrainNervousFirstInsured,
  medicalBrainNervousSecondInsured,
  medicalArthritisFirstInsured,
  medicalArthritisSecondInsured,
  medicalEyesEarsFirstInsured,
  medicalEyesEarsSecondInsured,
  medicalSectionADetailsFirstInsured,
  ...restOfQuestions
} = sharedOneAmericaQuestions;
const policyProductFundingQuestion = restOfQuestions.policyProductFundingSelection;

const questions: Record<string, PolicyFormQuestion> = {
  ...oneAmericaPreApplicationFormDefinition.questions,
  ...restOfQuestions,

  /* Section 6 - Policy Selections */
  policyNonforfeitureBenefit: {
    id: 'policy-nonforfeiture-benefit',
    questionRef: 'policyNonforfeitureBenefit',
    title: 'Nonforfeiture Benefit',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  policyProductFundingSelection: {
    ...policyProductFundingQuestion,
    options: [
      ...(policyProductFundingQuestion.options
        ? policyProductFundingQuestion.options.filter(el => el.id !== 'annuity-funding')
        : []),
      {
        id: 'annuity-funding',
        value:
          'Annuity Funding Whole Life (application I-31157 must also be completed) (if selected, proceed to #3)',
      },
    ],
  },
  policyInflationProtection: {
    id: 'policy-inflation-protection',
    questionRef: 'policyInflationProtection',
    title: 'Inflation Protection AOB/COB Options',
    type: 'radio',
    required: false,
    options: [
      { id: '0-3', value: '0%/3%' },
      { id: '0-5', value: '0%/5%' },
      { id: '3-3', value: '3%/3%' },
      { id: '5-5', value: '5%/5%' },
      { id: '3-no-cob', value: '3%/no COB' },
      { id: '5-no-cob', value: '5%/no COB' },
      { id: 'none', value: 'None' },
    ],
  },
  policyPaymentOptions: {
    id: 'policy-payment-options',
    questionRef: 'policyPaymentOptions',
    title: 'Premium Payment Options',
    type: 'multiselect',
    required: false,
    options: [
      { id: '1035-exchange', value: '1035 Exchange' },
      { id: 'ira-transfer', value: 'IRA Transfer' },
      { id: 'cash', value: 'Cash' },
    ],
  },
  lapseNoticeRecipientPhoneNumber: {
    id: 'lapse-notice-recipient-phone-number',
    questionRef: 'lapseNoticeRecipientPhoneNumber',
    title: 'Phone Number',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'lapseNoticeDesignation',
        value: 'designate',
        operator: 'equals',
      },
      required: {
        field: 'lapseNoticeDesignation',
        value: 'designate',
        operator: 'equals',
      },
    },
    validationType: 'phone',
  },

  /* Section 9 - Information Regarding other Coverage */
  coverageHasExistingInsurance: {
    id: 'coverage-has-existing-insurance',
    questionRef: 'coverageHasExistingInsurance',
    title: 'Do you have existing life insurance or annuity(ies) with this or any other company?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  acceleratedBenefitsReplaceLtc: {
    id: 'accelerated-benefits-replace-ltc',
    questionRef: 'acceleratedBenefitsReplaceLtc',
    title:
      'If applying for life insurance that offers accelerated death benefits, do you intend to replace any existing long-term care insurance?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  coverageExistingInsuranceDetails: {
    id: 'coverage-existing-insurance-details',
    questionRef: 'coverageExistingInsuranceDetails',
    title: 'List all insurance or annuities in force on Proposed Insured(s) below.',
    type: 'table',
    required: false,
    minRows: 0,
    maxRows: 6,
    columns: [
      {
        id: 'amount',
        title: 'Amount',
        type: 'number',
        required: false,
        placeholder: '$',
      },
      {
        id: 'issueYear',
        title: 'Issue Year',
        type: 'number',
        required: false,
      },
      {
        id: 'type',
        title: 'Type',
        type: 'text',
        required: false,
      },
      {
        id: 'companyPolicyInsured',
        title: 'Company / Policy Number / Insured',
        type: 'text',
        required: false,
      },
      {
        id: 'replacement',
        title: 'Replacement?',
        type: 'radio',
        required: false,
        options: [
          { id: 'yes', value: 'Yes' },
          { id: 'no', value: 'No' },
        ],
      },
      {
        id: 'exchange',
        title: 'Exchange?',
        type: 'radio',
        required: false,
        options: [
          { id: 'yes', value: 'Yes' },
          { id: 'no', value: 'No' },
        ],
      },
    ],
    conditional: {
      visible: {
        operator: 'or',
        conditions: [
          {
            field: 'coverageHasExistingInsurance',
            value: 'yes',
            operator: 'equals',
          },
          {
            field: 'acceleratedBenefitsReplaceLtc',
            value: 'yes',
            operator: 'equals',
          },
        ],
      },
    },
  },

  /* Signatures */
  signatureReceivedStatementConfirmation: {
    id: 'signature-received-statement-confirmation',
    questionRef: 'signatureReceivedStatementConfirmation',
    title: 'I (We) have received the',
    type: 'multiselect',
    required: true,
    options: [
      { id: 'outline-of-coverage', value: 'Outline of Coverage' },
      { id: 'ltc-personal-worksheet', value: 'Long-Term Care Personal Worksheet' },
      {
        id: 'things-to-know-before-buying-ltc',
        value: 'Things You Should Know Before You Buy Long-Term Care Insurance',
      },
      {
        id: 'notice-replacement-ltc-or-accident',
        value:
          'Notice to Applicant Regarding Replacement of Accident and Sickness or Long-Term Care Insurance, if applicable',
      },
      {
        id: 'ca-taking-care-of-tomorrow',
        value: 'California Department of Aging\'s "Taking Care of Tomorrow"',
      },
      { id: 'notice-of-information-practices', value: 'Notice of Information Practices' },
      { id: 'ltc-shoppers-guide', value: 'Long-Term Care Shoppers Guide' },
      {
        id: 'hicap',
        value: 'Health Insurance Counseling and Advocacy Program (HICAP)',
      },
    ],
  },

  medicalQuestionnaireSignatureConfirmation: {
    id: 'medical-questionnaire-signature-confirmation',
    questionRef: 'medicalQuestionnaireSignatureConfirmation',
    title: 'I (We) have received the',
    type: 'multiselect',
    required: true,
    options: [
      { id: 'outline-of-coverage', value: 'Outline of Coverage' },
      { id: 'ltc-personal-worksheet', value: 'Long-Term Care Personal Worksheet' },
      {
        id: 'things-to-know-before-buying-ltc',
        value: 'Things You Should Know Before You Buy Long-Term Care Insurance',
      },
      {
        id: 'notice-replacement-ltc-or-accident',
        value:
          'Notice to Applicant Regarding Replacement of Accident and Sickness or Long-Term Care Insurance, if applicable',
      },
      {
        id: 'ca-taking-care-of-tomorrow',
        value: 'California Department of Aging\'s "Taking Care of Tomorrow"',
      },
      { id: 'notice-of-information-practices', value: 'Notice of Information Practices' },
      { id: 'ltc-shoppers-guide', value: 'Long-Term Care Shoppers Guide' },
      {
        id: 'hicap',
        value: 'Health Insurance Counseling and Advocacy Program (HICAP)',
      },
    ],
  },

  /* Agent's Statement */
  temporaryInsuranceAgreementGiven: {
    id: 'temporary-insurance-agreement-given',
    questionRef: 'temporaryInsuranceAgreementGiven',
    title: 'Was a Temporary Insurance Agreement given?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },

  temporaryInsuranceAgreementGivenCA: {
    id: 'temporary-insurance-agreement-given-ca',
    questionRef: 'temporaryInsuranceAgreementGivenCA',
    title: 'Was a Temporary Insurance Agreement given?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    disabled: true,
    hidden: true,
    composite: {
      questionRefs: ['temporaryInsuranceAgreementGiven'],
      delimiter: ' ',
    },
  },

  insuranceAppliedForReplacement: {
    id: 'insurance-applied-for-replacement',
    questionRef: 'insuranceAppliedForReplacement',
    title:
      'Is the insurance applied for intended to replace or change any existing insurance or annuity policy not mentioned in Section 9?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },

  insuranceAppliedForReplacementCA: {
    id: 'insurance-applied-for-replacement-ca',
    questionRef: 'insuranceAppliedForReplacementCA',
    title:
      'Is the insurance applied for intended to replace or change any existing insurance or annuity policy not mentioned in Section 9?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    disabled: true,
    hidden: true,
    composite: {
      questionRefs: ['insuranceAppliedForReplacement'],
      delimiter: ' ',
    },
  },

  initialNotificationMailed: {
    id: 'initial-notification-mailed',
    questionRef: 'initialNotificationMailed',
    title:
      'Was the initial notification, for this meeting, mailed to the applicant 24 hours prior to this meeting?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },

  initialNotificationMailedCA: {
    id: 'initial-notification-mailed-ca',
    questionRef: 'initialNotificationMailedCA',
    title:
      'Was the initial notification, for this meeting, mailed to the applicant 24 hours prior to this meeting?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
    disabled: true,
    hidden: true,
    composite: {
      questionRefs: ['initialNotificationMailed'],
      delimiter: ' ',
    },
  },

  clientIncome: {
    id: 'client-income',
    questionRef: 'clientIncome',
    title: "Client's Income",
    type: 'text',
    required: false,
    placeholder: '$',
    validationType: 'currency',
  },

  clientNetWorth: {
    id: 'client-net-worth',
    questionRef: 'clientNetWorth',
    title: "Client's Net Worth",
    type: 'text',
    required: false,
    placeholder: '$',
    validationType: 'currency',
  },

  clientWitness: {
    id: 'client-witness',
    questionRef: 'clientWitness',
    title: "I personally witnessed the reading, completion, and signing of this application, and I recommend the Proposed Insured(s) for the insurance applied for except:",
    type: 'text',
    required: false,
  },

  agentFullName: {
    id: 'agent-full-name',
    questionRef: 'agentFullName',
    title: 'Print Full Name (Licensed Agent)',
    type: 'text',
    required: true,
  },

  agentNumber: {
    id: 'agent-number',
    questionRef: 'agentNumber',
    title: 'Agent Number (Licensed Agent)',
    type: 'text',
    required: true,
  },

  agentEmailAddress: {
    id: 'agent-email-address',
    questionRef: 'agentEmailAddress',
    title: 'Email Address (Licensed Agent)',
    type: 'text',
    required: true,
    validationType: 'email',
  },

  residentAgentFullName: {
    id: 'resident-agent-full-name',
    questionRef: 'residentAgentFullName',
    title: 'Print Full Name (Resident Agent, if required)',
    type: 'text',
    required: false,
  },

  residentAgentNumber: {
    id: 'resident-agent-number',
    questionRef: 'residentAgentNumber',
    title: 'Agent Number (Resident Agent, if required)',
    type: 'text',
    required: false,
  },

  residentAgentEmailAddress: {
    id: 'resident-agent-email-address',
    questionRef: 'residentAgentEmailAddress',
    title: 'Email Address (Resident Agent, if required)',
    type: 'text',
    required: false,
    validationType: 'email',
  },

  /* Medical Questions A - During past 10 years */
  ...questionsCategoryA,
  /* Medical Questions B - During past 2 years */
  ...questionsCategoryB,
  /* Medical Questions C  */
  ...questionsCategoryC,

  /* Medical Questions D  */
  medicalCriminalConvictionFirstInsured: {
    id: 'medical-criminal-conviction-first-insured',
    questionRef: 'medicalCriminalConvictionFirstInsured',
    title: 'Been convicted of a felony or misdemeanor?',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalCriminalConvictionSecondInsured: {
    id: 'medical-criminal-conviction-second-insured',
    questionRef: 'medicalCriminalConvictionSecondInsured',
    title: 'Been convicted of a felony or misdemeanor?',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },

  /* Medical Questions F  */
  ...questionsCategoryF,

  /* Medical Questions K  */
  ...questionsCategoryK,

  /* Medical Questions I  */
  medicalForeignTravelFirstInsured: {
    id: 'medical-foreign-travel-first-insured',
    questionRef: 'medicalForeignTravelFirstInsured',
    title:
      'Does any person proposed for insurance travel to or have residence in a foreign country or is any such travel or residency currently scheduled to occur within the next two years, with arrangements made, such as purchased plane tickets?',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalForeignTravelSecondInsured: {
    id: 'medical-foreign-travel-second-insured',
    questionRef: 'medicalForeignTravelSecondInsured',
    title:
      'Does any person proposed for insurance travel to or have residence in a foreign country or is any such travel or residency currently scheduled to occur within the next two years, with arrangements made, such as purchased plane tickets?',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },

  /* Section 13 - Premium (Annual/Single) (Rider) */
  ltcPremiumForNonforfeitureBenefitRider: {
    id: 'ltc-premium-for-nonforfeiture-benefit-rider',
    questionRef: 'ltcPremiumForNonforfeitureBenefitRider',
    title: 'Premium for Nonforfeiture Benefit Rider',
    type: 'text',
    placeholder: '$',
    validationType: 'currency',
  },

  /* Certificate of Insurability and Application for Delivery */
  certDeliveryPolicyNumber: {
    id: 'cert-delivery-policy-number',
    questionRef: 'certDeliveryPolicyNumber',
    title: 'Policy Number',
    type: 'text',
    required: true,
  },
  certDeliveryName: {
    id: 'cert-delivery-name',
    questionRef: 'certDeliveryName',
    title: 'Name',
    type: 'text',
    required: true,
  },
  certDeliveryEffectiveDate: {
    id: 'cert-delivery-effective-date',
    questionRef: 'certDeliveryEffectiveDate',
    title: 'I hereby apply for Delivery of said policy which is being made effective on',
    type: 'date',
    required: true,
  },
  certDeliveryStatementsTrue: {
    id: 'cert-delivery-statements-true',
    questionRef: 'certDeliveryStatementsTrue',
    title: 'THE STATEMENTS MADE IN ITEMS 1 & 2 ABOVE ARE TRUE',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'YES' },
      { id: 'no', value: 'NO' },
    ],
  },
  certDeliveryNoResponseDetails: {
    id: 'cert-delivery-no-response-details',
    questionRef: 'certDeliveryNoResponseDetails',
    title:
      'If the answer is "NO," list below details to a "NO" response in 1. or 2. above, indicating name of insured and as applicable, the illness, injury, dates, results of treatment, names and addresses of each facility and medical care provider',
    type: 'textarea',
    required: false,
    conditional: {
      visible: {
        field: 'certDeliveryStatementsTrue',
        value: 'no',
        operator: 'equals',
      },
      required: {
        field: 'certDeliveryStatementsTrue',
        value: 'no',
        operator: 'equals',
      },
    },
  },

  /* Long-Term Care Insurance Personal Worksheet */
  ltcResourcesForPremium: {
    id: 'ltc-resources-for-premium',
    questionRef: 'ltcResourcesForPremium',
    title: 'What resources will you use to pay your premium?',
    type: 'multiselect',
    required: false,
    options: [
      { id: 'current-income-employment', value: 'Current Income From Employment' },
      { id: 'savings', value: 'Savings' },
      { id: 'money-from-family', value: 'Money From My Family' },
      { id: 'current-income-investments', value: 'Current Income From Investments' },
      { id: 'sell-investments', value: 'Sell Investments' },
      { id: 'other', value: 'Other' },
      { id: 'current-income', value: 'Other Current Income' },
      { id: 'sell-assets', value: 'Sell Other Assets' },
    ],
  },
  ltcOtherResourceSpecify: {
    id: 'ltc-other-resource-specify',
    questionRef: 'ltcOtherResourceSpecify',
    title: 'Other (Please specify)',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'ltcResourcesForPremium',
        value: 'other',
        operator: 'contains',
      },
    },
  },
  ltcAffordIfSpouseDies: {
    id: 'ltc-afford-if-spouse-dies',
    questionRef: 'ltcAffordIfSpouseDies',
    title: 'Could you afford to keep this policy/rider if your spouse or partner dies first?',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
      { id: 'had-not-thought', value: 'Had Not Thought About It' },
      { id: 'do-not-know', value: 'Do Not Know' },
      { id: 'does-not-apply', value: 'Does Not Apply' },
    ],
  },
  ltcIncomeChangeAffectPremium: {
    id: 'ltc-income-change-affect-premium',
    questionRef: 'ltcIncomeChangeAffectPremium',
    title:
      'If you plan to pay premiums from your income, have you thought about how a change in your income would affect your ability to continue to pay the premium?',
    type: 'radio',
    required: false,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
      { id: 'do-not-know', value: 'Do Not Know' },
    ],
  },
  ltcPayForFutureCosts: {
    id: 'ltc-pay-for-future-costs',
    questionRef: 'ltcPayForFutureCosts',
    title:
      'If you do not buy inflation protection, how will you pay for the differences between future costs and your daily benefit amount?',
    type: 'multiselect',
    required: false,
    options: [
      { id: 'from-income', value: 'From My Income' },
      { id: 'from-savings', value: 'From Savings' },
      { id: 'from-investments', value: 'From Investments' },
      { id: 'sell-assets', value: 'Sell Other Assets' },
      { id: 'money-from-family', value: 'Money From My Family' },
      { id: 'other', value: 'Other' },
    ],
    conditional: {
      visible: {
        field: 'ltcBuyInflationProtection',
        value: 'no',
        operator: 'equals',
      },
    },
  },
  ltcPayForFutureCostsOther: {
    id: 'ltc-pay-for-future-costs-other',
    questionRef: 'ltcPayForFutureCostsOther',
    title: 'Other (Please specify)',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'ltcPayForFutureCosts',
        value: 'other',
        operator: 'contains',
      },
    },
  },

  // Long-Term Care Replacement Notice
  sabpBenefits: {
    id: 'ltc-replacement-notice-benefits',
    questionRef: 'sabpBenefits',
    title: '',
    type: 'multiselect',
    format: 'vertical',
    options: [
      { id: 'sabp-additional-benefits', value: 'Additional or different benefits' },
      { id: 'sabp-no-changes-in-benefits', value: 'No change in benefits, but lower premiums' },
      { id: 'sabp-fewer-benefits', value: 'Fewer benefits and lower premiums.' },
      { id: 'sabp-other', value: 'Other' },
    ],
  },
  sabpAdditionalBenefitsSpecify: {
    id: 'sabp-additional-benefits-specify',
    questionRef: 'sabpAdditionalBenefitsSpecify',
    title: 'Please specify (Additional or different benefits)',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'sabpBenefits',
        value: 'sabp-additional-benefits',
        operator: 'contains',
      },
    },
  },
  sabpOtherSpecify: {
    id: 'sabp-other-specify',
    questionRef: 'sabpOtherSpecify',
    title: 'Please specify (Other)',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'sabpBenefits',
        value: 'sabp-other',
        operator: 'contains',
      },
    },
  },

  sabpInsurerName: {
    id: 'sabp-insurer-name',
    questionRef: 'sabpInsurerName',
    title: 'Former Insurer Name',
    type: 'text',
    required: false,
  },

  // Notice Regarding Replacement
  replacementNoticeTable: {
    id: 'replacement-notice-table',
    questionRef: 'replacementNoticeTable',
    title: 'Policies involved in the replacement transaction:',
    type: 'table',
    required: true,
    minRows: 0,
    maxRows: 5,
    columns: [
      {
        id: 'company',
        title: 'Company',
        type: 'text',
        required: true,
      },
      {
        id: 'contract-number',
        title: 'Contract Number',
        type: 'text',
        required: true,
      },
      {
        id: 'insured',
        title: 'Insured',
        type: 'text',
        required: true,
      },
    ],
  },

  // Page 13
  page13FullName: {
    id: 'page13-full-name',
    questionRef: 'page13FullName',
    title: 'Full Name (as it appears on California license)',
    type: 'text',
    required: true,
  },

  page13CALicenseNumber: {
    id: 'page13-ca-license-number',
    questionRef: 'page13CALicenseNumber',
    title: 'CA License Number',
    type: 'text',
    required: true,
  },

  page13Address: {
    id: 'page13-address',
    questionRef: 'page13Address',
    title: 'Address',
    type: 'text',
    required: true,
    validationType: 'address',
  },

  page13TelephoneNumber: {
    id: 'page13-telephone-number',
    questionRef: 'page13TelephoneNumber',
    title: 'Telephone Number',
    type: 'text',
    required: true,
    validationType: 'phone',
  },

  page13PurposeOfVisit: {
    id: 'page13-purpose-of-visit',
    questionRef: 'page13PurposeOfVisit',
    title:
      'I am a licensed insurance agent. My purpose for coming to your home is to sell, discuss and/or deliver the following (indicate all that apply):',
    type: 'multiselect',
    required: false,
    options: [
      { id: 'life-insurance', value: 'Life insurance, including annuities' },
      { id: 'other-insurance-products', value: 'Other insurance products' },
      {
        id: 'other-financial-products',
        value: 'Other financial products',
      },
    ],
  },

  page13OtherInsuranceProductsSpecify: {
    id: 'page13-other-insurance-products-specify',
    questionRef: 'page13OtherInsuranceProductsSpecify',
    title: 'Please specify (Other insurance products)',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'page13PurposeOfVisit',
        value: 'other-insurance-products',
        operator: 'contains',
      },
    },
  },
  page13OtherFinancialProductsSpecify: {
    id: 'page13-other-financial-products-specify',
    questionRef: 'page13OtherFinancialProductsSpecify',
    title: 'Please specify (Other financial products)',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'page13PurposeOfVisit',
        value: 'other-financial-products',
        operator: 'contains',
      },
    },
  },

  // Applicant Disclosure and Acknowledgment
  adcPrintedName: {
    id: 'adc-printed-name',
    questionRef: 'adcPrintedName',
    title: 'Printed Name',
    type: 'text',
    required: true,
  },

  adcAgentName: {
    id: 'adc-agent-name',
    questionRef: 'adcAgentName',
    title: "Agent's Name",
    type: 'text',
    required: true,
  },

  adcHasSpouse: {
    id: 'adc-has-spouse',
    questionRef: 'adcHasSpouse',
    title:
      "Some documents may require a spouse's signature if you have one. Do you have a spouse, and can we send them these signature requests?",
    type: 'radio',
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },

  adcSpouseName: {
    id: 'adc-spouse-name',
    questionRef: 'adcSpouseName',
    title: 'Spouse Name',
    type: 'text',
    required: false,
    placeholder: 'Your spouse name',
    composite: {
      questionRefs: [
        'secondProposedInsuredFirstName',
        'secondProposedInsuredMiddleInitial',
        'secondProposedInsuredLastName',
      ],
      delimiter: ' ',
      allowAppendText: true,
      conditional: {
        field: 'fundingPolicyType',
        operator: 'equals' as const,
        value: FundingPolicyType.jointAssetCare,
      },
    },
    conditional: {
      visible: {
        field: 'adcHasSpouse',
        operator: 'equals',
        value: 'yes'
      },
      required: {
        field: 'adcHasSpouse',
        operator: 'equals',
        value: 'yes',
      },
    },
  },
  adcSpouseEmail: {
    id: 'adc-spouse-email',
    questionRef: 'adcSpouseEmail',
    title: 'Spouse Email',
    type: 'text',
    validationType: 'email',
    required: false,
    conditional: {
      visible: {
        field: 'adcHasSpouse',
        operator: 'equals',
        value: 'yes'
      },
      required: {
        field: 'adcHasSpouse',
        operator: 'equals',
        value: 'yes',
      },
    },
  },

  adcHasLegalRepresentative: {
    id: 'adc-has-legal-representative',
    questionRef: 'adcHasLegalRepresentative',
    title:
      "Some documents may require a legal representative's signature if you have one, such as a power of attorney. Do you have a legal representative who should sign, and can we send them these signature requests?",
    type: 'radio',
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },

  adcLegalRepresentativeName: {
    id: 'adc-legal-representative-name',
    questionRef: 'adcLegalRepresentativeName',
    title: 'Legal Representative Name',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'adcHasLegalRepresentative',
        operator: 'equals',
        value: 'yes',
      },
      required: {
        field: 'adcHasLegalRepresentative',
        operator: 'equals',
        value: 'yes',
      },
    },
  },

  adcLegalRepresentativeEmail: {
    id: 'adc-legal-representative-email',
    questionRef: 'adcLegalRepresentativeEmail',
    title: 'Legal Representative Email',
    type: 'text',
    validationType: 'email',
    required: false,
    conditional: {
      visible: {
        field: 'adcHasLegalRepresentative',
        operator: 'equals',
        value: 'yes',
      },
      required: {
        field: 'adcHasLegalRepresentative',
        operator: 'equals',
        value: 'yes',
      },
    },
  },

  adcVariableAnnuityOption: {
    id: 'adc-variable-annuity-option',
    questionRef: 'adcVariableAnnuityOption',
    title: 'If applying for a Variable Annuity, check here',
    type: 'multiselect',
    required: false,
    options: [{ id: 'yes', value: 'Yes' }],
  },

  alcoholAdditionalInformation: {
    id: 'alcohol-additional-information',
    questionRef: 'alcoholAdditionalInformation',
    title: 'Please provide any additional information which you feel is important',
    type: 'textarea',
    required: false,
  },

  nameAddressPhysician: {
    id: 'name-address-physician',
    questionRef: 'nameAddressPhysician',
    title: 'Name and address of designated Physician',
    type: 'text',
    required: false,
  },

  ltcPremiumInterval: {
    id: 'ltc-premium-interval',
    questionRef: 'ltcPremiumInterval',
    title: 'Interval for the Premium Amount for the Coverage Will Be',
    type: 'text',
    required: false,
    disabled: true,
    composite: {
      questionRefs: ['policyRecurringBillingMode'],
      delimiter: ' ',
    },
    conditional: {
      visible: {
        operator: 'and',
        conditions: [
          {
            field: 'policyProductFundingSelection',
            operator: 'equals',
            value: 'recurring-premium',
          },
          {
            field: 'policyRecurringBillingMode',
            operator: 'notEquals',
            value: 'annual',
          },
        ],
      },
    },
    helperText: 'You can change your payment interval from Policy Selection Billing Mode section.',
    parser: {
      type: 'valueMap',
      config: {
        'Monthly Bank Draft': 'Monthly',
      },
    }
  },

  ltcPremiumPerMonth: {
    id: 'ltc-premium-per-month',
    questionRef: 'ltcPremiumPerMonth',
    title: 'Premium Amount for the Coverage You Are Considering Will Be',
    type: 'text',
    required: false,
    placeholder: '$',
    validationType: 'currency',
    conditional: {
      visible: {
        operator: 'and',
        conditions: [
          {
            field: 'policyProductFundingSelection',
            operator: 'equals',
            value: 'recurring-premium',
          },
          {
            field: 'policyRecurringBillingMode',
            operator: 'notEquals',
            value: 'annual',
          },
        ],
      },
    },
  },
};

export const oneAmericaAssetCareCAFormDef: PolicyFormDefinition = {
  requiredParameters: oneAmericaSharedDefs.requiredParameters,
  sections: [
    ...oneAmericaPreApplicationFormDefinition.sections,
    sharedOneAmericaSections.firstProposedInsured,
    sharedOneAmericaSections.secondProposedInsured,
    sharedOneAmericaSections.owner,
    sharedOneAmericaSections.jointOwner,
    sharedOneAmericaSections.beneficiary,
    sharedOneAmericaSections.policySelections,
    sharedOneAmericaSections.annualIncome,
    {
      id: 'protection-against-lapse',
      title: 'Protection Against Unintentional Lapse',
      subsections: [
        {
          id: 'lapse-notice',
          title: 'Lapse Notice Designation',
          questionGroups: [
            {
              id: 'designation-choice',
              format: 'default',
              questions: ['lapseNoticeDesignation'],
            },
            {
              id: 'recipient-info',
              format: 'grid',
              questions: [
                'lapseNoticeRecipientName',
                'lapseNoticeRecipientPhoneNumber',
                'lapseNoticeRecipientAddress',
              ],
              conditional: {
                visible: {
                  field: 'lapseNoticeDesignation',
                  operator: 'equals',
                  value: 'designate',
                },
              },
            },
          ],
        },
      ],
    },
    /* 9 ▸ Information Regarding Other Coverage ------------------------------------------------------- */
    {
      id: 'other-coverage',
      title: 'Information Regarding Other Coverage',
      subsections: [
        {
          id: 'ltc-coverage',
          title: 'Long-Term Care Coverage',
          questionGroups: [
            {
              id: 'current-ltc',
              format: 'default',
              questions: ['coverageHasOtherLtc', 'coverageHadLtcLastYear'],
            },
            {
              id: 'ltc-details',
              format: 'grid',
              questions: ['coverageLtcCompanyName', 'coverageLtcLapseDate'],
              conditional: {
                visible: {
                  field: 'coverageHadLtcLastYear',
                  operator: 'equals',
                  value: 'yes',
                },
              },
            },
            {
              id: 'insurance-status',
              format: 'default',
              questions: ['coverageHasExistingInsurance', 'acceleratedBenefitsReplaceLtc'],
            },
            {
              id: 'insurance-details',
              format: 'default',
              questions: ['coverageExistingInsuranceDetails'],
              conditional: {
                visible: {
                  operator: 'or',
                  conditions: [
                    {
                      field: 'coverageHasOtherLtc',
                      operator: 'equals',
                      value: 'yes',
                    },
                    {
                      field: 'coverageHadLtcLastYear',
                      operator: 'equals',
                      value: 'yes',
                    },
                    {
                      field: 'coverageHasExistingInsurance',
                      operator: 'equals',
                      value: 'yes',
                    },
                    {
                      field: 'acceleratedBenefitsReplaceLtc',
                      operator: 'equals',
                      value: 'yes',
                    },
                  ],
                },
              },
            },
            {
              id: 'medicaid-status',
              format: 'default',
              questions: ['coverageEligibleMedicaid'],
            },
            {
              id: 'replacement-info',
              format: 'default',
              questions: ['coverageReplacingPolicy'],
            },
            {
              id: 'pending-status',
              format: 'default',
              questions: ['coverageApplicationPending'],
            },
            {
              id: 'pending-details',
              format: 'grid',
              questions: ['coveragePendingCompany', 'coveragePendingAmount'],
              conditional: {
                visible: {
                  field: 'coverageApplicationPending',
                  operator: 'equals',
                  value: 'yes',
                },
              },
            },
            {
              id: 'settlement-status',
              format: 'default',
              questions: ['coverageSoldLifeSettlement'],
            },
            {
              id: 'settlement-details',
              format: 'grid',
              questions: ['coverageSettlementCompany', 'coverageSettlementAmount'],
              conditional: {
                visible: {
                  field: 'coverageSoldLifeSettlement',
                  operator: 'equals',
                  value: 'yes',
                },
              },
            },
          ],
        },
      ],
    },
    sharedOneAmericaSections.specialRequest,
    sharedOneAmericaSections.acknowledgement,
    sharedOneAmericaSections.substituteW9,
    healthDataSections.medicalHealthInfoFirstProposedInsured,
    healthDataSections.medicalHealthInfoSecondProposedInsured,
    {
      id: 'medical-health-info-confirmation',
      title: 'Medical Questionnaire Confirmation',
      subsections: [
        {
          id: 'medical-health-signature',
          title: 'Signatures',
          questionGroups: [
            {
              id: 'medical-health-signature-group',
              format: 'default',
              questions: ['medicalQuestionnaireSignatureConfirmation'],
            },
          ],
        },
      ],
    },
    {
      id: 'licensed-agent-disclosure-purpose-of-visit',
      title: 'Licensed Agent Disclosure & Purpose of Visit',
      subsections: [
        {
          id: 'licensed-agent-disclosure',
          title: 'Disclosure',
          questionGroups: [
            {
              id: 'licensed-agent-disclosure-info-group',
              format: 'grid',
              questions: [
                'page13FullName',
                'page13CALicenseNumber',
                'page13Address',
                'page13TelephoneNumber',
              ],
            },
            {
              id: 'licensed-agent-disclosure-purpose-group',
              format: 'default',
              questions: [
                'page13PurposeOfVisit',
                'page13OtherInsuranceProductsSpecify',
                'page13OtherFinancialProductsSpecify',
              ],
            },
          ],
        },
      ],
    },
    sharedOneAmericaSections.usPatriotAct,
    // Applicant Disclosure and Acknowledgment
    {
      id: 'applicant-disclosure-and-acknowledgment',
      title: 'Applicant Disclosure and Acknowledgment',
      subsections: [
        {
          id: 'applicant-disclosure',
          title: 'Disclosure',
          questionGroups: [
            {
              id: 'applicant-disclosure-info-group',
              format: 'grid',
              questions: ['adcPrintedName', 'adcAgentName'],
            },
            {
              id: 'applicant-disclosure-has-spouse-group',
              title: 'Questions about your Spouse',
              format: 'default',
              questions: ['adcHasSpouse'],
            },
            {
              id: 'applicant-disclosure-spouse-info-group',
              format: 'grid',
              questions: ['adcSpouseName', 'adcSpouseEmail'],
            },
            {
              id: 'applicant-disclosure-has-representative-group',
              title: 'Questions about your Legal Representative',
              format: 'default',
              questions: ['adcHasLegalRepresentative'],
            },
            {
              id: 'applicant-disclosure-representative-info-group',
              format: 'grid',
              questions: ['adcLegalRepresentativeName', 'adcLegalRepresentativeEmail'],
            },
            {
              id: 'licensed-agent-disclosure-annuity-group',
              format: 'default',
              questions: ['adcVariableAnnuityOption'],
            },
          ],
        },
      ],
      conditional: {
        visible: {
          field: 'firstProposedInsuredBirthDate',
          operator: 'greaterThanOrEqual',
          value: 65,
        },
      },
    },
    sharedOneAmericaSections.authDisclosurePersonalInfo,
    sharedOneAmericaSections.rofVia1035Exchange,
    sharedOneAmericaSections.electronicPremiumPaymentAuthorization,
    /* 23 ▸ Notice Regarding Replacement ------------------------------------------------------- */
    {
      id: 'notice-regarding-replacement',
      title: 'Notice regarding replacement',
      subsections: [
        {
          id: 'insurance-policy-replacement',
          title: 'Replacing your life insurance policy or annuity?',
          questionGroups: [
            {
              id: 'insurance-policy-replacement-group',
              format: 'default',
              questions: ['replacementNoticeTable'],
            },
          ],
        },
      ],
      conditional: {
        visible: {
          field: 'policyReplacement',
          operator: 'equals',
          value: 'yes',
        },
      },
    },
    {
      id: 'designated-physician-consent',
      title: 'Consent',
      subsections: [
        {
          id: 'designated-physician-questions',
          title: 'Consent',
          questionGroups: [
            {
              id: 'designated-physician-consent-group',
              title: ' ',
              description:
                'I have read and I understand this Notice and Consent for Blood Testing Which Will Include AIDS (HIV) Antibody/Antigen Testing. I voluntarily consent to the withdrawal of blood from me by needle, the testing of that blood, and the disclosure of the test results as described above. I understand that I have the right to request and receive a copy of this authorization. A photocopy of this form will be as valid as the original.',
              format: 'default',
              questions: ['nameAddressPhysician'],
            },
          ],
        },
      ],
    },
    /* 23 ▸ LTC Insurance Personal Worksheet ------------------------------------------------------- */
    {
      id: 'ltc-insurance-personal-worksheet',
      title: 'Long-Term Care Insurance Personal Worksheet',
      subsections: [
        /* Premium Information */
        {
          id: 'ltc-worksheet-premium-info',
          title: 'Premium Information',
          questionGroups: [
            {
              id: 'ltc-worksheet-premium-info-group',
              format: 'default',
              questions: [
                'ltcPremiumInterval',
                'ltcPremiumPerMonth',
                'ltcPremiumPerYear',
                'ltcPremiumOneTime',
              ],
            },
          ],
        },

        /* Questions About Your Income */
        {
          id: 'ltc-worksheet-income',
          title: 'Questions About Your Income',
          questionGroups: [
            {
              id: 'ltc-worksheet-income-resources',
              format: 'default',
              questions: ['ltcResourcesForPremium', 'ltcOtherResourceSpecify'],
            },
            {
              id: 'ltc-worksheet-income-affordability',
              format: 'default',
              questions: ['ltcAffordIfSpouseDies'],
            },
            {
              id: 'ltc-worksheet-income-overview',
              format: 'grid',
              questions: ['ltcHouseholdIncome', 'ltcIncomeChangeExpectation'],
            },
            {
              id: 'ltc-worksheet-income-impact',
              format: 'grid',
              questions: ['ltcIncomeChangeAffectPremium', 'ltcBuyInflationProtection'],
            },
            {
              id: 'ltc-worksheet-future-costs',
              format: 'default',
              questions: ['ltcPayForFutureCosts', 'ltcPayForFutureCostsOther'],
            },
            {
              id: 'ltc-worksheet-elimination-period',
              format: 'default',
              questions: ['ltcPayDuringElimination'],
            },
            {
              id: 'ltc-worksheet-assets',
              format: 'grid',
              questions: ['ltcAssetsWorth', 'ltcAssetsChangeExpectation'],
            },
            {
              id: 'ltc-worksheet-disclosure-group',
              format: 'default',
              questions: ['ltcDisclosureStatement', 'ltcAgreeReviewed'],
            },
            {
              id: 'ltc-worksheet-agent-ack',
              format: 'grid',
              questions: [
                'ltcAgentExplained',
                'ltcAgentPrintedName',
                'personalWorksheetNotSuitable',
              ],
            },
          ],
        },
      ],
    },
    sharedOneAmericaSections.ltcPremiumAnnualSingle,
    {
      id: 'ltc-premium-annual-single-rider',
      title: 'Premium (Annual/Single) (Rider)',
      subsections: [
        {
          id: 'ltc-premium-fields-rider',
          title: 'Premium Details',
          questionGroups: [
            {
              id: 'ltc-premium-fields-group',
              format: 'grid',
              questions: ['ltcPremiumForThisRider', 'ltcPremiumForInflationProtectionRider2'],
            },
          ],
        },
      ],
    },
    /* 13 ▸ Signature Statement Confirmation ------------------------------------------------------- */
    {
      id: 'statement-of-understanding',
      title: "Agent's Statement",
      subsections: [
        {
          id: 'signature-confirmation',
          title: 'Signature Confirmation',
          questionGroups: [
            {
              id: 'signature-confirmation-question',
              format: 'default',
              questions: ['signatureReceivedStatementConfirmation'],
            },
          ],
        },
        {
          id: 'agents-statement',
          title: "Agent's Statement",
          questionGroups: [
            {
              id: 'insurance-confirmation-group',
              format: 'default',
              questions: [
                'temporaryInsuranceAgreementGiven',
                'insuranceAppliedForReplacement',
                'initialNotificationMailed',
              ],
            },
            {
              id: 'california-agent-statement-group',
              format: 'default',
              hidden: true,
              questions: [
                'temporaryInsuranceAgreementGivenCA',
                'insuranceAppliedForReplacementCA',
                'initialNotificationMailedCA',
              ],
            },
            {
              id: 'confirmation-client-income-group',
              format: 'grid',
              questions: ['clientIncome', 'clientNetWorth'],
            },
            {
              id: 'confirmation-client-withness-group',
              format: 'default',
              questions: ['clientWitness'],
            },
            {
              id: 'confirmation-agent-group',
              format: 'grid',
              questions: ['agentFullName', 'agentNumber'],
            },
            {
              id: 'confirmation-agent-email-group',
              format: 'default',
              questions: ['agentEmailAddress'],
            },
            {
              id: 'confirmation-resident-agent-group',
              format: 'grid',
              questions: ['residentAgentFullName', 'residentAgentNumber'],
            },
            {
              id: 'confirmation-resident-agent-email-group',
              format: 'default',
              questions: ['residentAgentEmailAddress'],
            },
          ],
        },
      ],
    },
    sharedOneAmericaSections.temporaryInsuranceAgreement,
    {
      id: 'ltc-replacement-notice',
      title: 'Long-Term Care Replacement Notice',
      subsections: [
        {
          id: 'applicant-to-producer-statement',
          title: 'Statement to Applicant by Producer',
          questionGroups: [
            {
              id: 'medical-health-signature-group-1',
              title: ' ',
              description:
                'COMPARISON TO YOUR CURRENT COVERAGE: I have reviewed your current long-term care coverage. To the best of my knowledge, the replacement of insurance involved in this transaction materially improves your position for the following reasons:',
              format: 'default',
              questions: ['sabpBenefits'],
            },
            {
              id: 'medical-health-signature-group-2',
              format: 'grid',
              questions: ['sabpAdditionalBenefitsSpecify', 'sabpOtherSpecify'],
            },
            {
              id: 'medical-health-signature-group-3',
              format: 'default',
              questions: ['sabpInsurerName'],
            },
          ],
        },
      ],
      conditional: {
        visible: {
          field: 'policyReplacement',
          operator: 'equals',
          value: 'yes',
        },
      },
    },
    sharedOneAmericaSections.w4p,
    sharedOneAmericaSections.alcoholUseSupplement,
    sharedOneAmericaSections.aviationSupplement,
    sharedOneAmericaSections.drugUseSupplement,
    sharedOneAmericaSections.armedForcesSupplement,
    sharedOneAmericaSections.avocationSupplement,
    sharedOneAmericaSections.foreignNationalsTravelSupplement,
    sharedOneAmericaSections.financialInstitutionDisclosure,
    /* 26 ▸ Certificate of Insurability and Application for Delivery ------------------------------------------------------- */
    {
      id: 'certificate-of-insurability',
      title: 'Certificate of Insurability and Application for Delivery',
      subsections: [
        /* Policy Information */
        {
          id: 'cert-delivery-policy-info',
          title:
            'Certificate of Insurability and Application for Delivery (Life and Health Insurance)',
          questionGroups: [
            {
              id: 'cert-delivery-policy-info-group',
              format: 'grid',
              questions: ['certDeliveryPolicyNumber', 'certDeliveryName'],
            },
            {
              id: 'cert-delivery-effective-date-group',
              format: 'default',
              questions: ['certDeliveryEffectiveDate'],
            },
            {
              id: 'cert-delivery-statements-group',
              title: 'Statements',
              description: `1. Except how amended, I represent that the responses provided on the application, as amended, including Part 1, Part 2 and Paramed Exam Form (if included) remain true and accurate.\n\n
2. I represent that since the earliest date of the application and the dates of all medical examinations required by the Company for this policy, no person proposed for insurance has:
    - been examined or evaluated by any physician or facility, nor is any exam or evaluation presently scheduled in the next 3 months;
    - applied for other life or health insurance, or for reinstatement which was declined or issued other than applied for, or had renewal refused.`,
              format: 'default',
              questions: ['certDeliveryStatementsTrue'],
            },
          ],
        },
      ],
    },
    sharedOneAmericaSections.assignment1035Exchange,
    sharedOneAmericaSections.acceptance1035Exchange,
    sharedOneAmericaSections.section1035PartialTransfer,
    sharedOneAmericaSections.powerOfAttorneyAffidavit,
    sharedOneAmericaSections.signatureAuthorityEntity,
  ],
  questions: {
    ...questions,
  },
};
